import { useState } from "react";
import { MenuItem, Select, Tooltip } from "@mui/material";
import FilterIcon from "assets/icons/tune.svg?react";
import {
  useSavedFilters,
  SavedFiltersSection,
  FilterSlidePanel,
} from "components/SavedFilters";
import {
  ExpenseEngagementFilter,
  ExpenseFilterCriteria,
  ExpenseSettlementFilter,
  DEFAULT_EXPENSE_CRITERIA,
} from "./types";
import styles from "./styles.module.scss";

const LS_KEY = "expensesRegister_filters";

const isActive = (c: ExpenseFilterCriteria) =>
  c.chapter !== "all" || c.settlement !== "all" || c.engagement !== "all";

interface Props {
  chapters: string[];
  criteria: ExpenseFilterCriteria;
  setCriteria: (c: ExpenseFilterCriteria) => void;
}

const ExpensesToolbar = ({ chapters, criteria, setCriteria }: Props) => {
  const [showFilter, setShowFilter] = useState(false);

  // Filtry własne (zapisane kombinacje panelu „Filtr"), trwałe w BE per user.
  const { saved, save, remove } =
    useSavedFilters<ExpenseFilterCriteria>(LS_KEY);

  // Menu Selectów portalowane nad overlay slajdu (z-index > 1300).
  const menuProps = {
    anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
    transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
    sx: { zIndex: 1400 },
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.left} />

      <div className={styles.right}>
        {/* Ikona Filtr — otwiera prawy slajd */}
        <Tooltip title="Filtr" placement="bottom">
          <div
            className={`${styles.icon} ${
              isActive(criteria) ? styles.iconActive : ""
            }`}
            onClick={() => setShowFilter(true)}
          >
            <FilterIcon />
          </div>
        </Tooltip>
      </div>

      <FilterSlidePanel
        open={showFilter}
        title="Filtr"
        onClose={() => setShowFilter(false)}
        showReset={isActive(criteria)}
        onReset={() => setCriteria(DEFAULT_EXPENSE_CRITERIA)}
      >
        <SavedFiltersSection<ExpenseFilterCriteria>
          saved={saved}
          onApply={(c) => {
            setCriteria(c);
            setShowFilter(false);
          }}
          onDelete={remove}
          onSave={(name) => save(name, criteria)}
          canSave={isActive(criteria)}
        />

        <div className={styles.group}>
          <span className={styles.label}>Rozdział</span>
          <Select
            size="small"
            MenuProps={menuProps}
            value={criteria.chapter}
            onChange={(e) =>
              setCriteria({ ...criteria, chapter: e.target.value })
            }
            className={styles.select}
          >
            <MenuItem value="all">Wszystkie</MenuItem>
            {chapters.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className={styles.group}>
          <span className={styles.label}>Rozliczenie</span>
          <Select
            size="small"
            MenuProps={menuProps}
            value={criteria.settlement}
            onChange={(e) =>
              setCriteria({
                ...criteria,
                settlement: e.target.value as ExpenseSettlementFilter,
              })
            }
            className={styles.select}
          >
            <MenuItem value="all">Wszystkie</MenuItem>
            <MenuItem value="settled">Rozliczone</MenuItem>
            <MenuItem value="partial">Częściowo</MenuItem>
            <MenuItem value="unsettled">Nierozliczone</MenuItem>
          </Select>
        </div>

        <div className={styles.group}>
          <span className={styles.label}>Zaangażowanie</span>
          <Select
            size="small"
            MenuProps={menuProps}
            value={criteria.engagement}
            onChange={(e) =>
              setCriteria({
                ...criteria,
                engagement: e.target.value as ExpenseEngagementFilter,
              })
            }
            className={styles.select}
          >
            <MenuItem value="all">Wszystkie</MenuItem>
            <MenuItem value="engaged">Zaangażowane</MenuItem>
            <MenuItem value="notengaged">Niezaangażowane</MenuItem>
          </Select>
        </div>
      </FilterSlidePanel>
    </div>
  );
};

export default ExpensesToolbar;
